import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Copy, Mail, MessageSquare, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";

const CHANNELS = ["email", "whatsapp", "sms"];
const CHANNEL_ICONS = { email: Mail, whatsapp: MessageSquare, sms: Phone };

const EMPTY = { name: "", channel: "email", subject: "", body: "", variables: [] };
const SEED = [
  { name: "Shortlist Confirmation", channel: "email", subject: "Great news, {{candidate_name}} — you're shortlisted", body: "Hi {{candidate_name}},\n\nCongratulations! You've been shortlisted for the {{job_title}} role at {{client_name}}. We'll share the interview slot shortly.\n\nRegards,\n{{recruiter_name}}\nPharmaco Hires", variables: ["candidate_name", "job_title", "client_name", "recruiter_name"] },
  { name: "Interview Invite", channel: "email", subject: "Interview scheduled for {{job_title}}", body: "Hi {{candidate_name}},\n\nYour {{round_name}} interview is scheduled for {{scheduled_at}} via {{mode}}. Link/venue: {{location}}.\n\nBest,\n{{recruiter_name}}", variables: ["candidate_name", "job_title", "round_name", "scheduled_at", "mode", "location", "recruiter_name"] },
  { name: "WhatsApp Follow-up", channel: "whatsapp", subject: "", body: "Hi {{candidate_name}}, this is {{recruiter_name}} from Pharmaco Hires — any update on the {{job_title}} interview? Happy to answer any questions.", variables: ["candidate_name", "recruiter_name", "job_title"] },
];

export default function AdminTemplates() {
  const [items, setItems] = useState([]);
  const [channelF, setChannelF] = useState("");
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/templates", { params: { channel: channelF || undefined } }).then((r) => setItems(r.data.items || []));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [channelF]);

  const save = async (data) => {
    try {
      if (data.id) await api.put(`/admin/templates/${data.id}`, data);
      else await api.post("/admin/templates", data);
      toast.success("Saved"); setEditing(null); load();
    } catch { toast.error("Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    await api.delete(`/admin/templates/${id}`); load();
  };

  const copy = (t) => {
    const txt = t.subject ? `Subject: ${t.subject}\n\n${t.body}` : t.body;
    navigator.clipboard.writeText(txt);
    toast.success("Copied to clipboard");
  };

  const seed = async () => {
    for (const t of SEED) await api.post("/admin/templates", t);
    toast.success("Seeded 3 sample templates"); load();
  };

  return (
    <div className="space-y-6" data-testid="admin-templates">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-slate-900">Message Templates</h1>
          <p className="text-sm text-slate-500">Reusable email / WhatsApp / SMS templates with variables like <code className="rounded bg-slate-100 px-1">{"{{candidate_name}}"}</code></p>
        </div>
        <div className="flex gap-2">
          {items.length === 0 && <Button variant="outline" onClick={seed}>Load sample templates</Button>}
          <Button onClick={() => setEditing({ ...EMPTY })} data-testid="template-new"><Plus className="mr-1 h-4 w-4" />New template</Button>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Select value={channelF || "all"} onValueChange={(v) => setChannelF(v === "all" ? "" : v)}>
          <SelectTrigger className="w-48"><SelectValue placeholder="Channel" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All channels</SelectItem>{CHANNELS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {items.map((t) => {
          const CI = CHANNEL_ICONS[t.channel] || Mail;
          return (
            <div key={t.id} className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`template-${t.id}`}>
              <div className="mb-2 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="grid h-9 w-9 place-items-center rounded-lg bg-primary/10 text-primary"><CI className="h-4 w-4" /></div>
                  <div>
                    <div className="font-heading text-base font-semibold text-slate-900">{t.name}</div>
                    <div className="text-xs text-slate-500">{t.channel} · {(t.variables || []).length} variables</div>
                  </div>
                </div>
                <Badge variant="outline">{t.channel}</Badge>
              </div>
              {t.subject && <div className="mt-2 text-sm text-slate-700"><span className="text-xs uppercase text-slate-500">Subject:</span> {t.subject}</div>}
              <div className="mt-2 max-h-24 overflow-hidden text-sm text-slate-600 whitespace-pre-line">{t.body}</div>
              {(t.variables || []).length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1">
                  {t.variables.map((v) => <Badge key={v} className="bg-secondary/15 text-secondary">{`{{${v}}}`}</Badge>)}
                </div>
              )}
              <div className="mt-3 flex justify-end gap-1 border-t border-slate-100 pt-3">
                <Button variant="ghost" size="sm" onClick={() => copy(t)}><Copy className="h-3.5 w-3.5" /></Button>
                <Button variant="ghost" size="sm" onClick={() => setEditing(t)}><Pencil className="h-3.5 w-3.5" /></Button>
                <Button variant="ghost" size="sm" onClick={() => remove(t.id)}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button>
              </div>
            </div>
          );
        })}
        {items.length === 0 && <div className="col-span-full rounded-xl border border-dashed border-slate-300 p-12 text-center text-slate-400">No templates yet. Click "Load sample templates" to start with common ones.</div>}
      </div>

      {editing && (
        <Dialog open={true} onOpenChange={(o) => !o && setEditing(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>{editing.id ? "Edit template" : "New template"}</DialogTitle></DialogHeader>
            <div className="grid gap-3">
              <div className="grid grid-cols-2 gap-3">
                <Input placeholder="Template name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="template-name" />
                <Select value={editing.channel} onValueChange={(v) => setEditing({ ...editing, channel: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CHANNELS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {editing.channel === "email" && <Input placeholder="Subject" value={editing.subject} onChange={(e) => setEditing({ ...editing, subject: e.target.value })} />}
              <Textarea placeholder="Body — use {{variable_name}} for placeholders" rows={8} value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })} data-testid="template-body" />
              <Input placeholder="Variables (comma-separated)" value={(editing.variables || []).join(", ")} onChange={(e) => setEditing({ ...editing, variables: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
              <Button onClick={() => save(editing)} data-testid="template-save">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
